import { useState } from 'react';
import Router from 'next/router';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { clearAuthenticatedSession } from 'lib/auth';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { Button, Link } from 'components/common';
import { Checkbox, Form, FormGroup, Select } from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';

const UserConsentForm = () => {
  const { dispatch } = useDispatch();
  const { language, messages, serverConfig, theme, user } = useReduxState();

  const [loading, setLoading] = useState(false);
  const [loadingDecline, setLoadingDecline] = useState(false);

  const decline = async () => {
    try {
      setLoadingDecline(true);
      const consent = { value: false };
      await api.post(`/auth/profile`, { consent });
      await api.post('/auth/logout');
      await clearAuthenticatedSession(dispatch);
      Router.push('/login');
    } catch (err) {
      setLoadingDecline(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const formIsValid = (formState) => {
    return (
      (formState.values.valueIsEmployee === true ||
        ((!serverConfig.accountAskForTerms ||
          formState.values.consentAcceptTerms) &&
          (!serverConfig.accountAskForConsent ||
            (formState.values.consentAcceptUsage &&
              formState.values.consentAcceptSharing)))) &&
      !formState.invalid
    );
  };

  const submit = async (values) => {
    try {
      setLoading(true);
      const consent = {
        valueIsEmployee: values.valueIsEmployee,
        value: !values.valueIsEmployee,
        terms: [
          values.consentAcceptUsage,
          values.consentAcceptSharing,
          values.consentAcceptTerms
        ]
      };
      const response = await api.post(`/auth/profile`, { consent });
      if (response.data && response.data.success) {
        dispatch(
          actions.setUser({
            ...user,
            consent: consent
          })
        );
        dispatch(
          actions.addNotice({
            text: messages.commonSavedSuccessfully,
            type: 'success'
          })
        );
      } else {
        await dispatch.handleErrorDispatch(null);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateConsent = (value) => {
    return value ? undefined : messages.commonFieldRequired;
  };

  const validateRequired = (value) => {
    return value !== undefined ? undefined : messages.commonFieldRequired;
  };

  return (
    <ModalPortal>
      <Form onSubmit={submit}>
        {({ formState }) => (
          <Modal
            title={
              serverConfig.accountAskForConsent
                ? messages.consent
                : messages.commonTerms
            }
            visible={true}
            width="medium"
          >
            <ModalContent>
              <div>
                {serverConfig.consentFormCustomContent ? (
                  <div
                    className="custom-content"
                    dangerouslySetInnerHTML={{
                      __html: serverConfig.consentFormCustomContent
                    }}
                  />
                ) : null}

                {serverConfig.accountAskForConsent &&
                serverConfig.accountAskForIsEmployee ? (
                  <div className="is-employee">
                    <div className="is-employee-question">
                      {messages.consentEmployeeDescription}
                    </div>
                    <div className="is-employee-input">
                      <Select
                        field="valueIsEmployee"
                        id="valueIsEmployee"
                        options={[
                          {
                            label: messages.commonYes,
                            value: true
                          },
                          {
                            label: messages.commonNo,
                            value: false
                          }
                        ]}
                        validate={validateRequired}
                        validateOnChange={true}
                      />
                    </div>
                  </div>
                ) : null}

                <div
                  className={
                    serverConfig.accountAskForConsent &&
                    serverConfig.accountAskForIsEmployee &&
                    (formState.values?.valueIsEmployee === false ||
                      serverConfig.accountAskForTerms)
                      ? 'consent-details'
                      : 'consent-details-plain'
                  }
                >
                  {serverConfig.accountAskForConsent &&
                  (!serverConfig.accountAskForIsEmployee ||
                    formState.values?.valueIsEmployee === false) ? (
                    <>
                      <FormGroup>
                        <Checkbox
                          error={formState.errors.consentAcceptUsage}
                          field="consentAcceptUsage"
                          id="consentAcceptUsage"
                          label={messages.consentUsage}
                          aria-required={true}
                          mask={(value) =>
                            value ? messages.consentUsage : false
                          }
                          validate={validateConsent}
                          validateOnChange={true}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Checkbox
                          error={formState.errors.consentAcceptSharing}
                          field="consentAcceptSharing"
                          id="consentAcceptSharing"
                          label={messages.consentSharing}
                          aria-required={true}
                          mask={(value) =>
                            value ? messages.consentSharing : false
                          }
                          validate={validateConsent}
                          validateOnChange={true}
                        />
                      </FormGroup>
                    </>
                  ) : null}

                  {serverConfig.accountAskForTerms ? (
                    <>
                      <FormGroup
                        style={{
                          marginTop:
                            serverConfig.accountAskForConsent &&
                            (!serverConfig.accountAskForIsEmployee ||
                              formState.values?.valueIsEmployee === false)
                              ? 15
                              : 0
                        }}
                      >
                        <Checkbox
                          error={formState.errors.consentAcceptTerms}
                          field="consentAcceptTerms"
                          id="consentAcceptTerms"
                          label={messages.consentTerms}
                          aria-required={true}
                          mask={(value) =>
                            value ? messages.consentTerms : false
                          }
                          validate={validateConsent}
                          validateOnChange={true}
                        />
                      </FormGroup>

                      <div className="consent-terms">
                        <div className="consent-terms-link">
                          <Link
                            external={true}
                            href={
                              language === 'fi-FI'
                                ? 'https://www.vuolearning.com/fi/user-terms'
                                : 'https://www.vuolearning.com/en/user-terms'
                            }
                            openInNewTab={true}
                            text={messages.consentTermsOfService}
                          />
                        </div>
                        <div className="consent-terms-link">
                          <Link
                            external={true}
                            href={
                              language === 'fi-FI'
                                ? 'https://www.vuolearning.com/fi/platform-privacy-policy'
                                : 'https://www.vuolearning.com/en/platform-privacy-policy'
                            }
                            openInNewTab={true}
                            text={messages.consentPrivacyPolicy}
                          />
                        </div>
                        {serverConfig.registerAdditionalTermsLinks
                          ? serverConfig.registerAdditionalTermsLinks.map(
                              (link) => (
                                <div className="consent-terms-link">
                                  <Link
                                    external={true}
                                    href={link.href}
                                    openInNewTab={true}
                                    text={link.text}
                                  />
                                </div>
                              )
                            )
                          : null}
                      </div>
                    </>
                  ) : null}
                </div>

                <style jsx>{`
                  .is-employee {
                    align-items: flex-start;
                    display: flex;
                    flex-direction: column;
                    gap: 10px 15px;
                  }
                  .is-employee-question {
                    flex-grow: 1;
                    flex-shrink: 1;
                    font-size: 1.5rem;
                    font-weight: 400;
                  }
                  .is-employee-input {
                    flex-grow: 0;
                    flex-shrink: 0;
                    width: 120px;
                  }
                  .consent-details {
                    border-top: 1px solid ${theme.lightGrey};
                    margin-top: 15px;
                    padding-top: 15px;
                  }
                  .custom-content {
                    font-size: 1.5rem;
                    margin-bottom: 25px;
                  }
                  .custom-content :global(p) {
                    margin: 0 0 15px;
                  }
                  .consent-terms {
                    margin-top: 5px;
                    padding-left: 25px;
                  }
                  .consent-terms-link:not(:last-child) {
                    margin-bottom: 5px;
                  }
                  @media (min-width: ${breakpoints.small}px) {
                    .is-employee {
                      align-items: center;
                      flex-direction: row;
                    }
                  }
                `}</style>
              </div>
            </ModalContent>

            <ModalFooter>
              {formState.values?.valueIsEmployee === false ? (
                <Button
                  category="danger"
                  disabled={loading}
                  loading={loadingDecline}
                  onClick={decline}
                  text={messages.consentDeclineAndSignOut}
                />
              ) : null}

              <Button
                category="success"
                disabled={!formIsValid(formState)}
                loading={loading}
                text={messages.commonAccept}
                type="submit"
              />
            </ModalFooter>
          </Modal>
        )}
      </Form>
    </ModalPortal>
  );
};

export default UserConsentForm;
